import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { toCsv, csvFilename, downloadCsv } from "utilities/csv";
import {
  MAX_IMPORT_ROWS,
  REQUIRED_FIELDS,
  IMPORT_FIELDS,
  parseImportCsv,
  valueRowCounts,
  matchRequestBody,
  initialMappings,
  unmappedProcessors,
  previewRequestBody,
  commitRequestBody,
  createCount,
  filterPreviewRows,
  PREVIEW_LABELS,
  REPORT_COLUMNS,
  setAllOwnership,
  ownershipState,
  ownershipLabel,
  templateCsv,
  TEMPLATE_FILENAME,
} from "utilities/midImport";

/**
 * SOUND-652 — bulk MID import into ONE company (internal-only, Phase 1).
 *
 * Upload → Confirm matches (POST /match) → Preview (POST /preview, writes
 * nothing) → Import (POST /, re-validates server-side) → result + report.
 *
 * Every decision is in utilities/midImport.js (tested). This file only renders.
 * Chrome follows StratusTransmitModal: portal, fixed header, scrolling body.
 * A backdrop click does NOT close it — that would throw away confirmed matches.
 *
 * Props: open, onClose(imported: boolean), companyId (the company imported INTO),
 *        companyName (optional, for the header)
 */

const STEPS = [
  { key: "upload", label: "Upload" },
  { key: "match", label: "Confirm matches" },
  { key: "preview", label: "Preview" },
];

const MATCH_TONE = {
  exact: "badge-success",
  normalized: "badge-info",
  prefix: "badge-info",
  ambiguous: "badge-warning",
  none: "badge-muted",
};

const LABEL_TONE = {
  create: "badge-success",
  exists: "badge-muted",
  conflict: "badge-destructive",
  error: "badge-warning",
};

const REQUIRED_LABELS = IMPORT_FIELDS.filter((f) => REQUIRED_FIELDS.includes(f.field)).map((f) => f.label);

const apiError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

function Heading({ children, hint }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</h4>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

function ErrorPanel({ title, message }) {
  if (!message) return null;
  return (
    <div className="rounded-lg bg-destructive/5 p-3 ring-1 ring-destructive/30" role="alert">
      <div className="flex items-start gap-2">
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div className="min-w-0">
          {title && <div className="text-sm font-semibold text-foreground">{title}</div>}
          <p className="mt-0.5 text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
    </div>
  );
}

function StepRail({ current }) {
  const idx = STEPS.findIndex((s) => s.key === current);
  return (
    <ol className="flex items-center gap-2 text-xs">
      {STEPS.map((s, i) => {
        const done = idx > i || current === "result";
        const active = s.key === current;
        return (
          <li key={s.key} className="flex items-center gap-2">
            {i > 0 && <span className="h-px w-4 bg-border" />}
            <span
              className={`grid h-5 w-5 place-items-center rounded-full text-[10px] font-semibold ${
                active
                  ? "bg-brand text-brand-foreground"
                  : done
                  ? "bg-success/15 text-success"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {i + 1}
            </span>
            <span className={active ? "font-medium text-foreground" : "text-muted-foreground"}>{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function MatchBadge({ match }) {
  return <span className={`${MATCH_TONE[match] || "badge-muted"} shrink-0 text-[10px]`}>{match || "none"}</span>;
}

/* ───────────────────────── Step 1: upload ───────────────────────── */

function UploadStep({ fileName, parsed, onFile, onTemplate, disabled }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const pick = (file) => {
    if (file) onFile(file);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          One row per MID, up to {MAX_IMPORT_ROWS}.{" "}
          <span className="text-foreground">Required columns: {REQUIRED_LABELS.join(", ")}.</span>
        </p>
        <button
          type="button"
          onClick={onTemplate}
          className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-brand hover:underline"
        >
          <Download className="h-4 w-4" />
          Download CSV template
        </button>
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !disabled) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled) pick(e.dataTransfer.files?.[0]);
        }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          dragging ? "border-brand bg-brand/5" : "border-border hover:bg-muted/30"
        }`}
      >
        <Upload className="h-6 w-6 text-muted-foreground" />
        <div className="text-sm font-medium text-foreground">
          {fileName ? "Choose a different file" : "Drop a CSV here, or click to choose"}
        </div>
        <div className="text-xs text-muted-foreground">
          Export from this tab works as a starting point. MID numbers keep their leading zeros.
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>

      {fileName && parsed && (
        <div className="surface rounded-xl p-4 ring-1 ring-border">
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="truncate font-medium text-foreground">{fileName}</span>
            {!parsed.error && (
              <span className="ml-auto text-muted-foreground tabular-nums">
                {parsed.rows.length.toLocaleString()} of {MAX_IMPORT_ROWS} rows
              </span>
            )}
          </div>
          {parsed.error ? (
            <div className="mt-3">
              <ErrorPanel title="This file can't be imported" message={parsed.error} />
            </div>
          ) : (
            parsed.mapping &&
            (parsed.mapping.unknown.length > 0 ||
              parsed.mapping.duplicates.length > 0 ||
              parsed.mapping.ignored?.length > 0) && (
              <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                {parsed.mapping.ignored?.map((ig) => (
                  <p key={ig.header}>
                    Ignored column <span className="font-medium text-foreground">{ig.header}</span>: {ig.reason}.
                  </p>
                ))}
                {parsed.mapping.unknown.length > 0 && (
                  <p>Ignored columns: {parsed.mapping.unknown.join(", ")}</p>
                )}
                {parsed.mapping.duplicates.length > 0 && (
                  <p className="text-warning">
                    Repeated columns, only the first is used: {parsed.mapping.duplicates.join(", ")}
                  </p>
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Step 2: confirm matches ───────────────────────── */

function MappingTable({ items, counts, children }) {
  return (
    <div className="overflow-hidden rounded-xl ring-1 ring-border">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
          <tr>
            <th className="w-[34%] px-3 py-2 font-medium">In the file</th>
            <th className="w-[16%] px-3 py-2 font-medium">Match</th>
            <th className="px-3 py-2 font-medium">Maps to</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.map((item) => (
            <tr key={item.value} className="align-middle">
              <td className="px-3 py-2">
                <div className="truncate font-medium text-foreground" title={item.value}>
                  {item.value}
                </div>
                <div className="text-xs text-muted-foreground tabular-nums">
                  {counts[item.value] || 0} row{counts[item.value] === 1 ? "" : "s"}
                </div>
              </td>
              <td className="px-3 py-2">
                <MatchBadge match={item.match} />
              </td>
              <td className="px-3 py-2">{children(item)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatchStep({
  rows,
  match,
  processorMappings,
  dbaMappings,
  onProcessor,
  onDba,
  unmapped,
  disabled,
}) {
  const processorCounts = useMemo(() => valueRowCounts(rows, "processor"), [rows]);
  const dbaCounts = useMemo(() => valueRowCounts(rows, "dba"), [rows]);
  const processorOptions = match?.options?.processors || [];
  const dbaOptions = match?.options?.dbas || [];
  const processors = match?.processors || [];
  const dbas = match?.dbas || [];

  return (
    <div className="space-y-6">
      <section>
        <Heading hint={unmapped.length ? `${unmapped.length} still to map` : "All mapped"}>Processors</Heading>
        <p className="mb-2 text-xs text-muted-foreground">
          Every processor in the file needs a registry match. Nothing is created — if one is missing, add it under
          Settings → Processors first.
        </p>
        {processors.length === 0 ? (
          <p className="text-sm text-muted-foreground">No processor values in the file.</p>
        ) : (
          <MappingTable items={processors} counts={processorCounts}>
            {(item) => (
              <select
                className={`select w-full py-1.5 text-sm ${
                  unmapped.includes(item.value) ? "ring-1 ring-warning/60" : ""
                }`}
                value={processorMappings[item.value] || ""}
                onChange={(e) => onProcessor(item.value, e.target.value)}
                disabled={disabled}
                aria-label={`Processor for ${item.value}`}
              >
                <option value="">Select a processor…</option>
                {processorOptions.map((o) => (
                  <option key={o.processorId} value={o.processorId}>
                    {o.name}
                  </option>
                ))}
              </select>
            )}
          </MappingTable>
        )}
      </section>

      <section>
        <Heading>DBAs</Heading>
        <p className="mb-2 text-xs text-muted-foreground">
          Only this company's DBAs are offered. "No DBA" imports the MID unlinked; it can be linked later.
        </p>
        {dbas.length === 0 ? (
          <p className="text-sm text-muted-foreground">No DBA values in the file — every MID imports without a DBA.</p>
        ) : (
          <>
            {dbaOptions.length === 0 && (
              <p className="mb-2 text-xs text-warning">This company has no DBAs, so every row imports as No DBA.</p>
            )}
            <MappingTable items={dbas} counts={dbaCounts}>
              {(item) => (
                <select
                  className="select w-full py-1.5 text-sm"
                  value={dbaMappings[item.value] || ""}
                  onChange={(e) => onDba(item.value, e.target.value || null)}
                  disabled={disabled}
                  aria-label={`DBA for ${item.value}`}
                >
                  <option value="">No DBA</option>
                  {dbaOptions.map((o) => (
                    <option key={o.dbaId} value={o.dbaId}>
                      {o.dbaName}
                    </option>
                  ))}
                </select>
              )}
            </MappingTable>
          </>
        )}
      </section>

    </div>
  );
}

/* ───────────────────────── Step 3: preview ───────────────────────── */

const COUNT_CARDS = [
  { key: "create", label: "Create" },
  { key: "exists", label: "Already here" },
  { key: "conflict", label: "Conflict" },
  { key: "error", label: "Error" },
];

function CountCards({ counts, active, onSelect }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {COUNT_CARDS.map((c) => {
        const selected = active === c.key;
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onSelect(selected ? "" : c.key)}
            className={`surface rounded-xl p-3 text-left ring-1 transition-colors ${
              selected ? "ring-brand" : "ring-border hover:bg-muted/30"
            }`}
            aria-pressed={selected}
          >
            <div className="text-xs text-muted-foreground">{c.label}</div>
            <div className="mt-0.5 text-xl font-semibold tabular-nums text-foreground">
              {(counts?.[c.key] ?? 0).toLocaleString()}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function PossibleDuplicate({ row, accepted, onToggle, disabled }) {
  const dup = row.possibleDuplicate;
  return (
    <div className="mt-2 rounded-lg bg-warning/5 p-2.5 ring-1 ring-warning/30">
      <div className="flex items-start gap-2 text-xs">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">Possible duplicate — the same number once leading zeros are stripped</div>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            <div>
              <div className="text-muted-foreground">In the file</div>
              <div className="font-mono tabular-nums text-foreground">{row.resolved?.mid || "—"}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Already on this processor</div>
              <Link
                to={`/mids/${dup.midId}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono tabular-nums text-brand hover:underline"
              >
                {dup.mid}
              </Link>
            </div>
          </div>
          <label className="mt-2 flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(accepted)}
              onChange={(e) => onToggle(row.rowNumber, e.target.checked)}
              disabled={disabled}
              className="h-3.5 w-3.5 rounded border-border text-brand focus:ring-brand/40"
            />
            <span className="text-foreground">Import anyway — these are different MIDs</span>
          </label>
        </div>
      </div>
    </div>
  );
}

/** Header "all on / all off" for Sound-owned; indeterminate when mixed. */
function OwnershipHeaderToggle({ state, onChange, disabled }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <label className="inline-flex cursor-pointer items-center gap-1.5" title="Set Sound-owned on every create row">
      <input
        ref={ref}
        type="checkbox"
        checked={state === "all"}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="h-3.5 w-3.5 rounded border-border text-brand focus:ring-brand/40"
        aria-label="Sound-owned: all on or all off"
      />
      Sound-owned
    </label>
  );
}

function PreviewStep({ preview, filter, onFilter, accepted, onAccept, ownership, onOwnership, onAllOwnership, disabled }) {
  const rows = filterPreviewRows(preview?.rows, filter);
  const dupCount = (preview?.rows || []).filter((r) => r.label === "create" && r.possibleDuplicate).length;

  return (
    <div className="space-y-4">
      <CountCards counts={preview?.counts} active={filter} onSelect={onFilter} />
      {dupCount > 0 && (
        <p className="text-xs text-warning">
          {dupCount} create row{dupCount === 1 ? " is a" : "s are"} possible duplicate{dupCount === 1 ? "" : "s"}. They
          are skipped unless you tick "Import anyway".
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Sound-owned</span> means the MID was placed through Sound's
        processor relationships. Leave it off for a client's own MIDs that we manage or monitor.
      </p>

      <div className="flex items-center gap-2">
        <select
          className="select w-auto py-1.5 text-sm"
          value={filter}
          onChange={(e) => onFilter(e.target.value)}
          aria-label="Filter rows"
        >
          <option value="">All rows</option>
          {Object.entries(PREVIEW_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground tabular-nums">
          {rows.length.toLocaleString()} row{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="overflow-x-auto rounded-xl ring-1 ring-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
            <tr>
              <th className="w-12 px-3 py-2 font-medium">Row</th>
              <th className="w-28 px-3 py-2 font-medium">Outcome</th>
              <th className="px-3 py-2 font-medium">MID</th>
              <th className="px-3 py-2 font-medium">Processor</th>
              <th className="px-3 py-2 font-medium">DBA</th>
              <th className="w-32 px-3 py-2 font-medium">
                <OwnershipHeaderToggle
                  state={ownershipState(preview?.rows, ownership)}
                  onChange={onAllOwnership}
                  disabled={disabled || !(preview?.counts?.create > 0)}
                />
              </th>
              <th className="px-3 py-2 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No rows with this outcome.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.rowNumber} className="align-top">
                  <td className="px-3 py-2 tabular-nums text-muted-foreground">{r.rowNumber}</td>
                  <td className="px-3 py-2">
                    <span className={`${LABEL_TONE[r.label] || "badge-muted"} text-[10px]`}>
                      {PREVIEW_LABELS[r.label]?.label || r.label}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs tabular-nums text-foreground">{r.resolved?.mid || "—"}</div>
                    {r.resolved?.descriptor && (
                      <div className="truncate text-xs text-muted-foreground">{r.resolved.descriptor}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{r.resolved?.processorName || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.resolved?.dbaName || "—"}</td>
                  <td className="px-3 py-2">
                    {r.label === "create" && (
                      <input
                        type="checkbox"
                        checked={ownership[r.rowNumber] === true}
                        onChange={(e) => onOwnership(r.rowNumber, e.target.checked)}
                        disabled={disabled}
                        className="h-4 w-4 rounded border-border text-brand focus:ring-brand/40"
                        aria-label={`Sound-owned, row ${r.rowNumber}`}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.reasons?.length > 0 && (
                      <ul className="space-y-0.5 text-xs text-muted-foreground">
                        {r.reasons.map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    )}
                    {r.label === "conflict" && r.conflictCompanyId && (
                      <Link
                        to={`/companies/${r.conflictCompanyId}?tab=mids`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-brand hover:underline"
                      >
                        Open that company
                      </Link>
                    )}
                    {r.label === "create" && r.possibleDuplicate && (
                      <PossibleDuplicate
                        row={r}
                        accepted={accepted[r.rowNumber]}
                        onToggle={onAccept}
                        disabled={disabled}
                      />
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ───────────────────────── Result ───────────────────────── */

const RESULT_CARDS = [
  { key: "created", label: "Created" },
  { key: "exists", label: "Already here" },
  { key: "conflict", label: "Conflict" },
  { key: "error", label: "Error" },
  { key: "skippedPossibleDuplicate", label: "Skipped duplicates" },
];

function ResultStep({ result, onReport }) {
  const created = result?.counts?.created ?? 0;
  const createdRows = (result?.rows || []).filter((r) => r.midId);
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg bg-success/5 p-3 ring-1 ring-success/30">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
        <div>
          <div className="text-sm font-semibold text-foreground">
            {created.toLocaleString()} MID{created === 1 ? "" : "s"} imported
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Imported MIDs appear in Risk and transaction data once they are bridged to the processor and gateway.
            Until then they show in the CRM only.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {RESULT_CARDS.map((c) => (
          <div key={c.key} className="surface rounded-xl p-3 ring-1 ring-border">
            <div className="text-xs text-muted-foreground">{c.label}</div>
            <div className="mt-0.5 text-xl font-semibold tabular-nums text-foreground">
              {(result?.counts?.[c.key] ?? 0).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
      {createdRows.length > 0 && (
        <div className="overflow-hidden rounded-xl ring-1 ring-border">
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="w-12 px-3 py-2 font-medium">Row</th>
                  <th className="px-3 py-2 font-medium">MID</th>
                  <th className="px-3 py-2 font-medium">Processor</th>
                  <th className="w-28 px-3 py-2 font-medium">Sound-owned</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {createdRows.map((r) => (
                  <tr key={r.rowNumber}>
                    <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{r.rowNumber}</td>
                    <td className="px-3 py-1.5">
                      <Link to={`/mids/${r.midId}`} className="font-mono text-xs tabular-nums text-brand hover:underline">
                        {r.resolved?.mid || r.midId}
                      </Link>
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground">{r.resolved?.processorName || "—"}</td>
                    <td className="px-3 py-1.5">
                      <span className={`${r.resolved?.isSoundOwned === true ? "badge-brand" : "badge-muted"} text-[10px]`}>
                        {ownershipLabel(r)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <button type="button" className="btn btn-outline" onClick={onReport}>
        <Download className="h-4 w-4" />
        Download row report
      </button>
    </div>
  );
}

/* ───────────────────────── Modal ───────────────────────── */

export default function MidImportModal({ open, onClose, companyId, companyName }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const userRef = useRef(auth.user);
  userRef.current = auth.user;

  const [step, setStep] = useState("upload");
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null);
  const [match, setMatch] = useState(null);
  const [processorMappings, setProcessorMappings] = useState({});
  const [dbaMappings, setDbaMappings] = useState({});
  // rowNumber -> boolean. Set ONLY in the preview, per create row; never from
  // the sheet (CPO 2026-10-30). Toggling does not re-run /preview — ownership
  // changes no label.
  const [ownership, setOwnership] = useState({});
  const [preview, setPreview] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [filter, setFilter] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Fresh state every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setStep("upload");
    setFileName("");
    setParsed(null);
    setMatch(null);
    setProcessorMappings({});
    setDbaMappings({});
    setOwnership({});
    setPreview(null);
    setAccepted({});
    setFilter("");
    setResult(null);
    setBusy(false);
    setError(null);
  }, [open]);

  const close = useCallback(() => {
    if (busy) return;
    onClose?.(Boolean(result));
  }, [busy, onClose, result]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  const rows = useMemo(() => parsed?.rows || [], [parsed]);
  const unmapped = useMemo(
    () => unmappedProcessors(rows, processorMappings, match?.options?.processors),
    [rows, processorMappings, match]
  );

  if (!open) return null;

  const post = (url, body) => makeApiRequest("post", url, userRef.current, effectiveCompanyId, body);

  const handleFile = async (file) => {
    setError(null);
    setFileName(file.name);
    try {
      setParsed(parseImportCsv(await file.text()));
    } catch {
      setParsed({ rows: [], mapping: null, error: "The file could not be read." });
    }
  };

  const handleTemplate = () => downloadCsv(TEMPLATE_FILENAME, templateCsv());

  const goMatch = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await post(apiRoutes.midImportMatch(companyId), matchRequestBody(rows));
      const initial = initialMappings(res.data);
      setMatch(res.data);
      setProcessorMappings(initial.processorMappings);
      setDbaMappings(initial.dbaMappings);
      setStep("match");
    } catch (err) {
      setError({ title: "Could not match processors and DBAs", message: apiError(err, "Please try again.") });
    } finally {
      setBusy(false);
    }
  };

  const previewArgs = { fileName, rows, processorMappings, dbaMappings };

  const goPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await post(apiRoutes.midImportPreview(companyId), previewRequestBody(previewArgs));
      setPreview(res.data);
      setAccepted({});
      setOwnership({});
      setFilter("");
      setStep("preview");
    } catch (err) {
      setError({ title: "Preview failed", message: apiError(err, "Please try again.") });
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await post(apiRoutes.midImportCommit(companyId), commitRequestBody(previewArgs, accepted, preview?.rows, ownership));
      setResult(res.data);
      setStep("result");
    } catch (err) {
      setError({ title: "Import failed", message: apiError(err, "Please try again.") });
    } finally {
      setBusy(false);
    }
  };

  const downloadReport = () =>
    downloadCsv(csvFilename("mid-import-report"), toCsv(result?.rows || [], REPORT_COLUMNS));

  const back = () => {
    setError(null);
    if (step === "preview") {
      setPreview(null);
      setStep("match");
    } else if (step === "match") {
      setStep("upload");
    }
  };

  const toCreate = createCount(preview?.rows, accepted);
  const canUpload = Boolean(parsed && !parsed.error && rows.length);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="surface flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl shadow-lg ring-1 ring-border"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mid-import-title"
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border p-5">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-brand/15 text-brand">
              <Upload className="h-5 w-5" />
            </div>
            <div className="space-y-2">
              <div>
                <h3 id="mid-import-title" className="text-lg font-semibold">
                  Import MIDs
                </h3>
                {companyName && <p className="mt-0.5 text-sm text-muted-foreground">Into {companyName}</p>}
              </div>
              {step !== "result" && <StepRail current={step} />}
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="btn btn-ghost h-8 w-8 shrink-0 p-0"
            aria-label="Close"
            disabled={busy}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          {error && <ErrorPanel title={error.title} message={error.message} />}
          {step === "upload" && (
            <UploadStep
              fileName={fileName}
              parsed={parsed}
              onFile={handleFile}
              onTemplate={handleTemplate}
              disabled={busy}
            />
          )}
          {step === "match" && (
            <MatchStep
              rows={rows}
              match={match}
              processorMappings={processorMappings}
              dbaMappings={dbaMappings}
              onProcessor={(value, id) => setProcessorMappings((m) => ({ ...m, [value]: id }))}
              onDba={(value, id) => setDbaMappings((m) => ({ ...m, [value]: id }))}
              unmapped={unmapped}
              disabled={busy}
            />
          )}
          {step === "preview" && (
            <PreviewStep
              preview={preview}
              filter={filter}
              onFilter={setFilter}
              accepted={accepted}
              onAccept={(rowNumber, checked) => setAccepted((a) => ({ ...a, [rowNumber]: checked }))}
              ownership={ownership}
              onOwnership={(rowNumber, checked) => setOwnership((o) => ({ ...o, [rowNumber]: checked }))}
              onAllOwnership={(on) => setOwnership(setAllOwnership(preview?.rows, on))}
              disabled={busy}
            />
          )}
          {step === "result" && <ResultStep result={result} onReport={downloadReport} />}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border p-4">
          <div className="text-xs text-muted-foreground">
            {step === "preview" && "Nothing has been written yet."}
            {step === "match" && unmapped.length > 0 && (
              <span className="text-warning">
                Map {unmapped.length} more processor value{unmapped.length === 1 ? "" : "s"} to continue.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(step === "match" || step === "preview") && (
              <button type="button" className="btn btn-ghost" onClick={back} disabled={busy}>
                Back
              </button>
            )}
            {step === "upload" && (
              <button type="button" className="btn btn-primary" onClick={goMatch} disabled={!canUpload || busy}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Continue
              </button>
            )}
            {step === "match" && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={goPreview}
                disabled={unmapped.length > 0 || busy}
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Preview
              </button>
            )}
            {step === "preview" && (
              <button type="button" className="btn btn-primary" onClick={commit} disabled={toCreate === 0 || busy}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {toCreate === 0 ? "Nothing to import" : `Import ${toCreate.toLocaleString()} MID${toCreate === 1 ? "" : "s"}`}
              </button>
            )}
            {step === "result" && (
              <button type="button" className="btn btn-primary" onClick={close}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

